import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { useSimpleToast, getErrorMessage } from '../components/Toast';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Holidays({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [holidays, setHolidays] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dashboardStats, setDashboardStats] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    holiday_type: 'Public Holiday',
    is_mandatory: true,
    description: ''
  });

  useEffect(() => {
    fetchHolidays();
    fetchDashboardStats();
  }, [selectedYear]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/holidays?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data.sort((a, b) => new Date(a.date) - new Date(b.date)));
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (selectedHoliday) {
        await axios.put(`${API}/holidays/${selectedHoliday.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Holiday updated successfully!', 'success');
      } else {
        await axios.post(`${API}/holidays`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showToast('Holiday created successfully!', 'success');
      }
      setShowForm(false);
      setSelectedHoliday(null);
      resetForm();
      fetchHolidays();
    } catch (error) {
      console.error('Save holiday error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleEdit = (holiday) => {
    setSelectedHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      holiday_type: holiday.holiday_type,
      is_mandatory: holiday.is_mandatory,
      description: holiday.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Holiday deleted successfully!', 'success');
      fetchHolidays();
    } catch (error) {
      console.error('Delete holiday error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      date: '',
      holiday_type: 'Public Holiday',
      is_mandatory: true,
      description: ''
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      'Public Holiday': 'bg-red-100 text-red-700',
      'Optional Holiday': 'bg-[#2b338030] text-blue-700',
      'Restricted Holiday': 'bg-yellow-100 text-yellow-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'Public Holiday': '🎉',
      'Optional Holiday': '🎈',
      'Restricted Holiday': '⚠️'
    };
    return icons[type] || '📅';
  };

  const getMonthName = (date) => {
    return new Date(date).toLocaleDateString('en-US', { month: 'long' });
  };

  const getDayName = (date) => {
    return new Date(date).toLocaleDateString('en-US', { weekday: 'long' });
  };

  const groupByMonth = () => {
    const grouped = {};
    holidays.forEach(holiday => {
      const month = getMonthName(holiday.date);
      if (!grouped[month]) grouped[month] = [];
      grouped[month].push(holiday);
    });
    return grouped;
  };

  const years = [2024, 2025, 2026, 2027];

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Holiday Calendar</h1>
          <div className="flex gap-3 items-center">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border rounded-lg"
            >
              {years.map(year => <option key={year} value={year}>{year}</option>)}
            </select>
            {(user.role === 'Admin' || user.role === 'HR Manager') && (
              <button
                onClick={() => { setShowForm(true); setSelectedHoliday(null); resetForm(); }}
                className="px-6 py-2 rounded-lg text-white hover:opacity-90"
                style={{backgroundColor: '#2b3380'}}
              >
                Add Holiday
              </button>
            )}
          </div>
        </div>

        {/* Holiday Calendar Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Holiday Calendar Overview"
            icon="🎉"
            bgColor="#03a9f4"
            stats={[
              {
                label: 'Upcoming Holidays',
                value: dashboardStats.upcoming_holidays || 0,
                subtitle: 'Next 30 days',
                color: '#03a9f4'
              },
              {
                label: 'Total Holidays',
                value: holidays.length || 0,
                subtitle: `Year ${selectedYear}`,
                color: '#00acc1'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Will be on leave',
                color: '#2b3380'
              },
              {
                label: 'Holiday Types',
                value: '3',
                subtitle: 'Public/Optional/Comp-off',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                  {selectedHoliday ? 'Edit Holiday' : 'Add Holiday'}
                </h2>
                <button onClick={() => { setShowForm(false); setSelectedHoliday(null); }} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required placeholder="Holiday Name" className="w-full px-3 py-2 border rounded-lg" />
                <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required className="w-full px-3 py-2 border rounded-lg" />
                <select value={formData.holiday_type} onChange={(e) => setFormData({...formData, holiday_type: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  <option value="Public Holiday">Public Holiday</option>
                  <option value="Optional Holiday">Optional Holiday</option>
                  <option value="Restricted Holiday">Restricted Holiday</option>
                </select>
                <label className="flex items-center">
                  <input type="checkbox" checked={formData.is_mandatory} onChange={(e) => setFormData({...formData, is_mandatory: e.target.checked})} className="mr-2" />
                  Mandatory for all employees
                </label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="3" placeholder="Description (optional)" className="w-full px-3 py-2 border rounded-lg"></textarea>
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => { setShowForm(false); setSelectedHoliday(null); }} className="px-6 py-2 border rounded-lg">Cancel</button>
                  <button type="submit" className="px-6 py-2 rounded-lg text-white" style={{backgroundColor: '#2b3380'}}>Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Holiday List */}
        {holidays.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
            <p className="text-lg">No holidays found for {selectedYear}.</p>
            {(user.role === 'Admin' || user.role === 'HR Manager') && (
              <p className="mt-2">Add holidays to get started.</p>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupByMonth()).map(([month, monthHolidays]) => (
              <div key={month}>
                <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>{month}</h2>
                <div className="space-y-3">
                  {monthHolidays.map(holiday => (
                    <div key={holiday.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="text-center">
                            <div className="text-4xl font-bold" style={{color: '#2b3380'}}>
                              {new Date(holiday.date).getDate()}
                            </div>
                            <div className="text-sm text-gray-500">{getDayName(holiday.date)}</div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-2xl">{getTypeIcon(holiday.holiday_type)}</span>
                              <h3 className="text-xl font-bold">{holiday.name}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(holiday.holiday_type)}`}>
                                {holiday.holiday_type}
                              </span>
                              {holiday.is_mandatory && (
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Mandatory</span>
                              )}
                            </div>
                            {holiday.description && (
                              <p className="text-gray-600 text-sm">{holiday.description}</p>
                            )}
                          </div>
                        </div>
                        {(user.role === 'Admin' || user.role === 'HR Manager') && (
                          <div className="flex gap-2 ml-4">
                            <button onClick={() => handleEdit(holiday)} className="text-[#2b3380] hover:text-[#2b3380] text-sm font-medium">Edit</button>
                            <button onClick={() => handleDelete(holiday.id)} className="text-red-600 hover:text-red-800 text-sm font-medium">Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Holidays;